import React from 'react'
import { useTranslation } from 'react-i18next'

import { Column } from 'primereact/column'
import { TreeTable, TreeTableSortMeta } from 'primereact/treetable'

import {
  AttachmentDownloadButton,
  AttachmentOpenInTabButton,
} from '../../../../../components/attachments'
import { DateBodyTemplate } from '../../../../../components/tableTemplates'
import { useColumnToggleDirect } from '../../../../../hooks/useColumnToggle'
import { highlightMatches } from '../../../../../utils/history/highlightMatches'
import { EmptyMessage } from '../../../../../utils/wrapper/ConfigurableTable/EmptyMessage'
import { HistoryNode } from '../../../common/interface'
import { useOrderHistory } from '../../data'
import { DataHistoryOrder } from '../../data/model'
import { statusBodyTemplate } from './templates'

const STORAGE_KEY = 'history-orders-visible-columns'

type Props = {
  data?: HistoryNode<DataHistoryOrder>[]
  setSearch: (value: string) => void
  setExpandedKeys: (value: Record<string, boolean>) => void
  expandedKeys: Record<string, boolean>
  search: string
}

export const Table: React.FC<Props> = ({
  data,
  setSearch,
  expandedKeys,
  search,
  setExpandedKeys,
}) => {
  const { t } = useTranslation()
  const { data: historyData } = useOrderHistory()

  const toggleableColumns = [
    {
      field: 'createdAt',
      label: t('ordersHistory.table.columns.createdAt.header'),
    },
    {
      field: 'createdBy',
      label: t('ordersHistory.table.columns.createdBy.header'),
    },
  ]

  const { show, navigationColumnHeader } = useColumnToggleDirect(
    toggleableColumns,
    STORAGE_KEY,
    true,
  )

  const actionBodyTemplate = (rowData: HistoryNode<DataHistoryOrder>) => {
    if (!rowData.data?.attachment) return null

    return (
      <>
        <AttachmentOpenInTabButton
          key={`att-tab-${rowData.key}`}
          fullUrl={rowData.data.attachment.fullUrl}
          mimeType={rowData.data.attachment.mimeType}
          fileName={rowData.data.attachment.fileName}
        />

        <AttachmentDownloadButton
          key={`att-download-${rowData.key}`}
          fullUrl={rowData.data.attachment.fullUrl}
          mimeType={rowData.data.attachment.mimeType}
          fileName={rowData.data.attachment.fileName}
        />
      </>
    )
  }

  const clearFilter = () => {
    setSearch('')
    setExpandedKeys(expandedKeys)
  }

  const emptyMessage = (
    <EmptyMessage
      title={t('ordersHistory.table.emptyMessageTitle')}
      details={t('ordersHistory.table.emptyMessageDetails')}
      value={historyData}
      clearFilter={clearFilter}
    />
  )

  const sortMeta: TreeTableSortMeta[] = [{ field: 'createdAt', order: 1 }]

  return (
    <div className="card">
      <TreeTable
        value={data}
        currentPageReportTemplate={t('common.table.numberOfEntries')}
        filterMode="lenient"
        paginator
        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
        sortMode="multiple"
        multiSortMeta={sortMeta}
        expandedKeys={expandedKeys}
        onToggle={e => setExpandedKeys(e.value)}
        rows={20}
        rowsPerPageOptions={[20, 50, 100]}
        style={{ fontSize: '90%' }}
        scrollable
        emptyMessage={emptyMessage}
      >
        {/* Process */}
        <Column
          field="process"
          header={t('ordersHistory.table.columns.process.header')}
          expander
          body={(rowData: any) => {
            return t(
              `ordersHistory.table.columns.process.options.${rowData.data.process.toLowerCase()}`,
            )
          }}
          style={{ width: '300px' }}
        />

        {/* Id */}
        {show('id') && (
          <Column
            field="id"
            header={t('ordersHistory.table.columns.id.header')}
            sortable
            style={{ width: '150px' }}
            body={(node: HistoryNode<DataHistoryOrder>) => (
              <div>{highlightMatches(node.data?.id!, search)}</div>
            )}
          />
        )}

        {/* Created at */}
        {show('createdAt') && (
          <Column
            field="createdAt"
            header={t('ordersHistory.table.columns.createdAt.header')}
            body={(rowData: any) =>
              DateBodyTemplate(rowData.data.createdAt, undefined, true)
            }
            sortable
            style={{ width: '160px' }}
          />
        )}

        {/* Created by */}
        {show('createdBy') && (
          <Column
            field="createdBy"
            header={t('ordersHistory.table.columns.createdBy.header')}
            sortable
            style={{ width: '250px' }}
            body={(node: HistoryNode<DataHistoryOrder>) => (
              <div className="break-words">
                {highlightMatches(node.data?.createdBy!, search)}
              </div>
            )}
          />
        )}

        {/* Status */}
        {show('status') && (
          <Column
            field="status"
            header={t('ordersHistory.table.columns.status.header')}
            body={(node: HistoryNode<DataHistoryOrder>) =>
              statusBodyTemplate(node.data?.status)
            }
            sortable
            style={{ width: '250px' }}
          />
        )}

        {/* Attachment */}
        <Column
          field="attachment"
          body={actionBodyTemplate}
          style={{ width: '100px' }}
          header={
            <div className="flex justify-content-between align-items-center gap-2 w-full">
              <span>{t('ordersHistory.table.columns.attachment.header')}</span>
              {navigationColumnHeader}
            </div>
          }
        />
      </TreeTable>
    </div>
  )
}
